import { useEffect, useMemo, useRef, useState } from "react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OnboardingGate from "@/components/auth/OnboardingGate";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useProfile } from "@/lib/profile/useProfile";
import { useRaelaThread, useRaelaThreads } from "@/lib/messaging/useRaela";
import { useThreadAutoSummary } from "@/lib/messaging/useThreadSummary";
import MessageRating from "@/components/raela/MessageRating";
import type { AppId } from "@/lib/apps";
import { Plus, Send, Sparkles, Trash2, ShieldAlert } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const RaelaInner = () => {
  const { profile } = useProfile();
  const { threads, loading: threadsLoading, create, archive } = useRaelaThreads();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const scroller = useRef<HTMLDivElement>(null);

  const appCtx: AppId = (profile?.active_app ?? profile?.primary_app ?? "fryqnc") as AppId;

  // Pick first thread once threads load
  useEffect(() => {
    if (!activeId && threads.length > 0) setActiveId(threads[0].id);
  }, [threads, activeId]);

  const { messages, traceByMessageId, loading, sending, send } = useRaelaThread(activeId, appCtx);
  const activeThread = threads.find((t) => t.id === activeId) ?? null;
  useThreadAutoSummary(activeId, messages, activeThread?.title ?? null);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [messages.length]);

  const onNew = async () => {
    const { id } = await create(appCtx);
    if (id) setActiveId(id);
  };

  const onSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;

    // Auto-create a thread if none active
    let id = activeId;
    if (!id) {
      const created = await create(appCtx);
      id = created.id;
      if (!id) return;
      setActiveId(id);
    }
    setDraft("");
    const { error } = await send(text);
    if (error) setDraft(text);
  };

  const onDelete = async (id: string) => {
    await archive(id);
    if (id === activeId) setActiveId(null);
  };

  const headerSubtitle = useMemo(() => {
    const map: Record<AppId, string> = {
      fryqnc: "your Fryqnc companion",
      pulsynq: "your Pulsynq companion",
      mindlynq: "your MindLynq companion",
      ablelynq: "your Ablelynq companion",
      deafwav: "your Deafwav companion",
      fullynq: "your Fullynq companion",
      jadesynq: "your Jadesynq companion",
      openlynq: "your Openlynq companion",
      spiritsynq: "your Spiritsynq companion",
    };
    return map[appCtx];
  }, [appCtx]);

  return (
    <main className="mx-auto grid h-[calc(100vh-8rem)] max-w-5xl grid-cols-1 gap-4 px-6 py-6 md:grid-cols-[260px_1fr]">
      {/* Sidebar */}
      <aside className="hidden flex-col rounded-3xl border border-border bg-card p-4 md:flex">
        <Button onClick={onNew} className="mb-3 w-full" size="sm">
          <Plus className="h-4 w-4" /> New chat
        </Button>
        <div className="flex-1 overflow-y-auto pr-1">
          {threadsLoading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : threads.length === 0 ? (
            <p className="text-xs text-muted-foreground">No chats yet.</p>
          ) : (
            <ul className="space-y-1">
              {threads.map((t) => {
                const active = t.id === activeId;
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => setActiveId(t.id)}
                      className={`group flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm transition ${
                        active ? "bg-accent text-foreground" : "hover:bg-accent/60 text-muted-foreground"
                      }`}
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {t.title ??
                          `Chat · ${formatDistanceToNow(new Date(t.updated_at), { addSuffix: true })}`}
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          void onDelete(t.id);
                        }}
                        className="rounded p-1 opacity-0 hover:bg-background group-hover:opacity-100"
                        aria-label="Delete chat"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>

      {/* Chat panel */}
      <section className="flex flex-col rounded-3xl border border-border bg-card">
        <header className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-foreground text-background">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium leading-tight">RAELA</p>
              <p className="text-xs text-muted-foreground">{headerSubtitle}</p>
            </div>
          </div>
          <Button onClick={onNew} variant="outline" size="sm" className="md:hidden">
            <Plus className="h-4 w-4" /> New
          </Button>
        </header>

        <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : messages.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-6 text-center">
              <p className="text-sm font-medium">Hi, I'm RAELA.</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Tell me what's on your mind. I'm here to listen.
              </p>
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.role === "user";
              return (
                <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                      mine ? "bg-foreground text-background" : "bg-muted text-foreground"
                    }`}
                  >
                    {m.flag_severity === "crisis" && !mine && (
                      <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-destructive">
                        <ShieldAlert className="h-3.5 w-3.5" /> Crisis resources
                      </div>
                    )}
                    <p className="whitespace-pre-wrap">{m.content}</p>
                  </div>
                  {!mine && (
                    <MessageRating messageId={m.id} traceId={traceByMessageId[m.id] ?? null} />
                  )}
                </div>
              );
            })
          )}
          {sending && (
            <div className="flex justify-start">
              <div className="rounded-2xl bg-muted px-4 py-3 text-sm text-muted-foreground">
                RAELA is thinking…
              </div>
            </div>
          )}
        </div>

        <div className="flex items-end gap-2 border-t border-border px-5 py-4">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void onSend();
              }
            }}
            placeholder="Talk to RAELA…"
            className="min-h-[44px] flex-1 resize-none"
            rows={1}
            disabled={sending}
          />
          <Button onClick={onSend} disabled={sending || !draft.trim()} size="icon">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </section>
    </main>
  );
};

const Raela = () => (
  <OnboardingGate>
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <RaelaInner />
      <SiteFooter />
    </div>
  </OnboardingGate>
);

export default Raela;
